import { useCallback, useEffect, useState } from "react";
import { dataViewService } from "../services/dataViewService";
import datasets from "../../../constants/DataSet";
import {
  DOC_STATUS_FILTER_ALL,
  normalizeDocStatusFilterValue,
  shouldShowDocStatusFeature,
} from "../utils/docStatus";
import { hasAdminEditAudit } from "../utils/auditHelpers";

const DEFAULT_PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 300;
const ACTIVE_DATASET_STORAGE_KEY = "research-data-view-active-dataset";
const validDatasetIds = new Set(datasets.map((dataset) => dataset.id));

const getErrorMessage = (error, fallbackMessage) =>
  error?.message || error?.data?.message || fallbackMessage;

const buildInitialPagination = () => ({
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  total: null,
  total_is_estimated: false,
  total_pages: null,
  has_more: false,
  cursor: null,
  next_cursor: null,
  cursor_history: [null],
});

export const useDataView = ({
  initialDataset = "clinical_trials",
  mode = "research",
} = {}) => {
  const [activeDataset, setActiveDataset] = useState(() => {
    if (typeof window === "undefined") {
      return initialDataset;
    }

    const storedDataset = localStorage.getItem(ACTIVE_DATASET_STORAGE_KEY);
    if (storedDataset && validDatasetIds.has(storedDataset)) {
      return storedDataset;
    }

    localStorage.removeItem(ACTIVE_DATASET_STORAGE_KEY);
    return validDatasetIds.has(initialDataset) ? initialDataset : datasets[0]?.id;
  });
  const [searchInput, setSearchInput] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(DOC_STATUS_FILTER_ALL);
  const [records, setRecords] = useState([]);
  const [cachedRecordsByDataset, setCachedRecordsByDataset] = useState({});
  const [listError, setListError] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const [editableDetailRecord, setEditableDetailRecord] = useState(null);
  const [detailAuditMetadata, setDetailAuditMetadata] = useState(null);
  const [isViewingOriginalRecord, setIsViewingOriginalRecord] = useState(false);
  const [currentDetailSnapshot, setCurrentDetailSnapshot] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [pagination, setPagination] = useState(buildInitialPagination);
  const [refreshVersion, setRefreshVersion] = useState(0);

  const resetDetailState = () => {
    setDetailRecord(null);
    setEditableDetailRecord(null);
    setDetailAuditMetadata(null);
    setIsViewingOriginalRecord(false);
    setCurrentDetailSnapshot(null);
    setDetailLoading(false);
    setDetailError("");
  };

  const clearDetailState = () => {
    resetDetailState();
  };

  const beginDetailLoading = () => {
    resetDetailState();
    setDetailLoading(true);
  };

  const canFilterByDocStatus = mode === "admin" && shouldShowDocStatusFeature(activeDataset);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    localStorage.setItem(ACTIVE_DATASET_STORAGE_KEY, activeDataset);
  }, [activeDataset]);

  useEffect(() => {
    let isActive = true;

    const fetchRecords = async () => {
      setListLoading(true);
      setListError("");

      try {
        if (submittedQuery) {
          const searchParams = {
            dataset: activeDataset,
            query: submittedQuery,
            per_page: pagination.per_page,
            cursor: pagination.cursor,
          };

          if (canFilterByDocStatus) {
            searchParams.docStatus = selectedStatus;
          }

          if (!pagination.cursor) {
            searchParams.page = pagination.page;
          }

          const response = await dataViewService.searchRecords(searchParams, mode);

          if (!isActive) {
            return;
          }

          const payload = response?.data || {};
          const nextRecords = payload.records || [];
          const nextPagination = payload.pagination || {};

          setRecords(nextRecords);
          setPagination((current) => ({
            ...current,
            total:
              typeof nextPagination.total === "number"
                ? nextPagination.total
                : current.total,
            total_is_estimated:
              typeof nextPagination.total_is_estimated === "boolean"
                ? nextPagination.total_is_estimated
                : current.total_is_estimated,
            total_pages:
              typeof nextPagination.total_pages === "number"
                ? nextPagination.total_pages
                : current.total_pages,
            has_more: Boolean(nextPagination.has_more),
            next_cursor: nextPagination.next_cursor || null,
          }));
          return;
        }

        const listParams = {
          dataset: activeDataset,
          per_page: pagination.per_page,
          cursor: pagination.cursor,
        };

        if (canFilterByDocStatus) {
          listParams.docStatus = selectedStatus;
        }

        if (!pagination.cursor) {
          listParams.page = pagination.page;
        }

        const response = await dataViewService.listRecords(listParams, mode);

        if (!isActive) {
          return;
        }

        const payload = response?.data || {};
        const nextRecords = payload.records || [];
        const nextPagination = payload.pagination || {};

        setRecords(nextRecords);
        setPagination((current) => ({
          ...current,
          total:
            typeof nextPagination.total === "number"
              ? nextPagination.total
              : current.total,
          total_is_estimated:
            typeof nextPagination.total_is_estimated === "boolean"
              ? nextPagination.total_is_estimated
              : current.total_is_estimated,
          total_pages:
            typeof nextPagination.total_pages === "number"
              ? nextPagination.total_pages
              : current.total_pages,
          has_more: Boolean(nextPagination.has_more),
          next_cursor: nextPagination.next_cursor || null,
        }));
      } catch (error) {
        if (!isActive) {
          return;
        }

        setRecords([]);
        setPagination((current) => ({
          ...current,
          has_more: false,
          next_cursor: null,
        }));
        setListError(getErrorMessage(error, "Unable to load dataset records right now."));
      } finally {
        if (isActive) {
          setListLoading(false);
        }
      }
    };

    fetchRecords();

    return () => {
      isActive = false;
    };
  }, [
    activeDataset,
    canFilterByDocStatus,
    mode,
    pagination.cursor,
    pagination.page,
    pagination.per_page,
    submittedQuery,
    selectedStatus,
    refreshVersion,
  ]);

  useEffect(() => {
    let isActive = true;

    if (!selectedRecordId) {
      resetDetailState();
      return () => {
        isActive = false;
      };
    }

    const cachedDatasetRecords = cachedRecordsByDataset[activeDataset] || [];
    const seededDetailRecord =
      cachedDatasetRecords.find((record) => record._record_id === selectedRecordId) ||
      records.find((record) => record._record_id === selectedRecordId) ||
      null;

    const shouldSeedDetailRecord = activeDataset !== "company";
    const nextSeededDetailRecord = shouldSeedDetailRecord ? seededDetailRecord : null;

    setDetailRecord(nextSeededDetailRecord);
    setEditableDetailRecord(
      nextSeededDetailRecord?._editable_record || nextSeededDetailRecord || null
    );
    setDetailAuditMetadata(nextSeededDetailRecord?._audit_metadata || null);
    setIsViewingOriginalRecord(false);
    setCurrentDetailSnapshot(null);
    setDetailError("");

    const shouldFetchDetail =
      activeDataset === "company" || mode !== "research" || !seededDetailRecord;

    if (!shouldFetchDetail) {
      setDetailLoading(false);
      return () => {
        isActive = false;
      };
    }

    setDetailLoading(true);

    const fetchRecordDetail = async () => {
      try {
        const response = await dataViewService.getRecordDetail(
          {
            dataset: activeDataset,
            record_id: selectedRecordId,
          },
          mode
        );

        if (!isActive) {
          return;
        }

        const payload = response?.data || {};
        const nextRecord = payload.record || null;
        if (!nextRecord) {
          setDetailError("Unable to load record details right now.");
          return;
        }

        setDetailRecord(nextRecord);
        setEditableDetailRecord(payload.editable_record || nextRecord);
        setDetailAuditMetadata(payload.audit_metadata || null);
      } catch (error) {
        if (!isActive) {
          return;
        }
        if (!seededDetailRecord) {
          setDetailError(getErrorMessage(error, "Unable to load record details right now."));
        }
      } finally {
        if (isActive) {
          setDetailLoading(false);
        }
      }
    };

    fetchRecordDetail();

    return () => {
      isActive = false;
    };
  }, [activeDataset, cachedRecordsByDataset, mode, records, selectedRecordId]);

  const resetPagination = () => {
    resetDetailState();
    setPagination((current) => ({
      ...buildInitialPagination(),
      per_page: current.per_page,
    }));
  };

  const updateDataset = (datasetId) => {
    if (!validDatasetIds.has(datasetId)) {
      return;
    }

    setActiveDataset(datasetId);
    setSelectedRecordId(null);
    setSearchInput("");
    setSubmittedQuery("");
    setSelectedStatus(DOC_STATUS_FILTER_ALL);
    resetPagination();
  };

  const openLinkedRecord = (datasetKey, recordId) => {
    if (!datasetKey || !recordId || !validDatasetIds.has(datasetKey)) {
      return;
    }

    if (activeDataset === "company" || datasetKey === "company") {
      clearDetailState();
    }

    if (datasetKey !== activeDataset) {
      setActiveDataset(datasetKey);
      setSearchInput("");
      setSubmittedQuery("");
      setSelectedStatus(DOC_STATUS_FILTER_ALL);
      setPagination((current) => ({
        ...buildInitialPagination(),
        per_page: current.per_page,
      }));
    }

    setSelectedRecordId(recordId);
    setIsViewingOriginalRecord(false);
    setCurrentDetailSnapshot(null);
    setDetailError("");

    if (datasetKey === "company") {
      setDetailLoading(true);
    }
  };

  const applySearchQuery = useCallback((query, { force = false } = {}) => {
    if (!force && query === submittedQuery) {
      return;
    }

    setSelectedRecordId(null);
    resetDetailState();
    setPagination((current) => ({
      ...buildInitialPagination(),
      per_page: current.per_page,
    }));
    setSubmittedQuery(query);
  }, [submittedQuery]);

  useEffect(() => {
    const nextQuery = searchInput.trim();

    if (nextQuery === submittedQuery) {
      return undefined;
    }

    const debounceTimer = setTimeout(() => {
      applySearchQuery(nextQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [applySearchQuery, searchInput, submittedQuery]);

  const submitSearch = (event) => {
    event.preventDefault();
    applySearchQuery(searchInput.trim(), { force: true });
  };

  const clearSearch = () => {
    setSearchInput("");
    setSelectedRecordId(null);
    resetDetailState();
    setPagination((current) => ({
      ...buildInitialPagination(),
      per_page: current.per_page,
    }));
    setSubmittedQuery("");
    setSelectedStatus(DOC_STATUS_FILTER_ALL);
  };

  const handleStatusChange = (nextStatus) => {
    const normalizedStatus = normalizeDocStatusFilterValue(nextStatus);

    if (normalizedStatus === selectedStatus) {
      return;
    }

    setSelectedRecordId(null);
    resetDetailState();
    setPagination((current) => ({
      ...buildInitialPagination(),
      per_page: current.per_page,
    }));
    setSelectedStatus(normalizedStatus);
  };

  const goToPage = (nextPageOrUpdater) => {
    setPagination((current) => {
      const resolvedNextPage =
        typeof nextPageOrUpdater === "function"
          ? nextPageOrUpdater(current.page)
          : nextPageOrUpdater;

      const nextPage = Math.max(1, resolvedNextPage);

      if (nextPage === current.page) {
        return current;
      }

      if (nextPage === 1) {
        return {
          ...current,
          page: 1,
          cursor: null,
          next_cursor: current.page === 1 ? current.next_cursor : null,
          has_more: current.page === 1 ? current.has_more : false,
          cursor_history: [null],
        };
      }

      if (nextPage === current.page + 1) {
        if (!current.next_cursor) {
          return current;
        }

        const nextCursorHistory = current.cursor_history.slice(0, current.page);
        nextCursorHistory[current.page] = current.next_cursor;

        return {
          ...current,
          page: nextPage,
          cursor: current.next_cursor,
          next_cursor: null,
          has_more: false,
          cursor_history: nextCursorHistory,
        };
      }

      if (nextPage < current.page) {
        return {
          ...current,
          page: nextPage,
          cursor: current.cursor_history[nextPage - 1] || null,
          next_cursor: null,
          has_more: true,
        };
      }

      const targetCursor = current.cursor_history[nextPage - 1];
      if (targetCursor === undefined) {
        return current;
      }

      return {
        ...current,
        page: nextPage,
        cursor: targetCursor,
        next_cursor: null,
        has_more: true,
      };
    });
  };

  const goToLastPage = () => {
    setPagination((current) => {
      const lastPage =
        typeof current.total_pages === "number"
          ? Math.max(current.total_pages, 1)
          : null;

      if (!lastPage || current.page >= lastPage) {
        return current;
      }

      return {
        ...current,
        page: lastPage,
        cursor: null,
        next_cursor: null,
        has_more: false,
      };
    });
  };

  const updatePerPage = (nextPerPage) => {
    const parsedPerPage = Number(nextPerPage);

    if (!Number.isInteger(parsedPerPage) || parsedPerPage <= 0) {
      return;
    }

    setSelectedRecordId(null);
    resetDetailState();
    setPagination(() => ({
      ...buildInitialPagination(),
      per_page: parsedPerPage,
    }));
  };

  const refreshData = () => {
    setRefreshVersion((current) => current + 1);
  };

  const applyRecordUpdate = (
    nextRecord,
    nextEditableRecord = nextRecord,
    nextAuditMetadata = null
  ) => {
    const targetRecordId = nextRecord?._record_id || nextEditableRecord?._record_id;
    if (!targetRecordId) {
      return;
    }

    const replaceRecord = (currentRecord) =>
      currentRecord && currentRecord._record_id === targetRecordId
        ? {
            ...currentRecord,
            ...nextRecord,
            _editable_record: nextEditableRecord,
            _audit_metadata: nextAuditMetadata,
          }
        : currentRecord;

    setRecords((currentRecords) => currentRecords.map(replaceRecord));
    setCachedRecordsByDataset((current) => {
      const currentDatasetRecords = current[activeDataset];
      if (!Array.isArray(currentDatasetRecords)) {
        return current;
      }

      return {
        ...current,
        [activeDataset]: currentDatasetRecords.map(replaceRecord),
      };
    });
    setDetailRecord(nextRecord);
    setEditableDetailRecord(nextEditableRecord);
    setDetailAuditMetadata(nextAuditMetadata);
    setIsViewingOriginalRecord(false);
    setCurrentDetailSnapshot(null);
    setDetailError("");
  };

  const viewOriginalRecord = async () => {
    if (mode !== "admin" || !selectedRecordId || !hasAdminEditAudit(detailAuditMetadata)) {
      return;
    }

    setDetailLoading(true);
    setDetailError("");

    try {
      const response = await dataViewService.getOriginalRecordDetail(
        {
          dataset: activeDataset,
          record_id: selectedRecordId,
        },
        mode
      );
      const payload = response?.data || {};

      setCurrentDetailSnapshot({
        record: detailRecord,
        editableRecord: editableDetailRecord,
        auditMetadata: detailAuditMetadata,
      });
      setDetailRecord(payload.record || null);
      setEditableDetailRecord(payload.editable_record || payload.record || null);
      setDetailAuditMetadata(payload.audit_metadata || null);
      setIsViewingOriginalRecord(true);
    } catch (error) {
      setDetailError(getErrorMessage(error, "Unable to load original record right now."));
    } finally {
      setDetailLoading(false);
    }
  };

  const viewCurrentRecord = () => {
    if (!currentDetailSnapshot) {
      return;
    }

    setDetailRecord(currentDetailSnapshot.record || null);
    setEditableDetailRecord(
      currentDetailSnapshot.editableRecord || currentDetailSnapshot.record || null
    );
    setDetailAuditMetadata(currentDetailSnapshot.auditMetadata || null);
    setIsViewingOriginalRecord(false);
    setCurrentDetailSnapshot(null);
    setDetailError("");
  };

  return {
    activeDataset,
    records,
    listError,
    listLoading,
    detailError,
    detailLoading,
    selectedRecordId,
    detailRecord,
    editableDetailRecord,
    detailAuditMetadata,
    isViewingOriginalRecord,
    searchInput,
    submittedQuery,
    selectedStatus,
    pagination,
    setSearchInput,
    setSelectedRecordId,
    submitSearch,
    clearSearch,
    handleStatusChange,
    goToPage,
    goToLastPage,
    updatePerPage,
    updateDataset,
    openLinkedRecord,
    applyRecordUpdate,
    viewOriginalRecord,
    viewCurrentRecord,
    refreshData,
    clearDetailState,
    beginDetailLoading,
  };
};
